import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Form,
  Label,
  Input,
  Button,
  FormFeedback,
  FormText,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import {useNavigate, useLocation } from 'react-router-dom'

import {
  createClient,
  updateClient,
  getClientById,
  setUpdateClient,
  setCreateClient,
  setGetClientById,
} from '../../../../redux/actions/client';
import { getCategories } from '../../../../redux/actions/provider';
import Loading from '../../../../components/Loading/Loading';
import { useClientValidation } from './formik';
import { format } from '../../../../utils/rut.util';
import { formatFormRUT } from '../../../../utils/utils';
import { REGION_OPTIONS } from '../../constants';

function ClientForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const clientCreateState = useSelector((state) => state.client.create);
  const clientUpdateState = useSelector((state) => state.client.update);
  const detailState = useSelector((state) => state.client.getById);
  const categoriesState = useSelector((state) => state.provider.categories);
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [communes, setCommunes] = useState([]);
  const [companyCommunes, setCompanyCommunes] = useState([]);

  const formik = useClientValidation((values) => {
    const formData = { ...values };

    // Create client info
    const clientInfo = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      rut: format(formData.rut, { dots: false }),
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      region: formData.region,
      commune: formData.commune,
      isCompany: formData.isCompany,
      interests: formData.interests, // Categorías de interés del cliente

      // Add companyInfo object if isCompany is true
      ...(formData.isCompany && {
        companyInfo: {
          name: formData.companyName,
          rut: format(formData.companyRut, { dots: false }),
          address: formData.companyAddress,
          region: formData.companyRegion,
          commune: formData.companyComuna,
        },
      }),
    };

    // Send data to server
    setLoading(true);
    if (clientId) {
      dispatch(updateClient(clientId, clientInfo));
    } else {
      dispatch(createClient(clientInfo));
    }
  });

  // Update communes when region changes
  useEffect(() => {
    if (formik.values.region) {
      const selectedRegion = REGION_OPTIONS.find(
        (region) => region.region === formik.values.region,
      );
      if (selectedRegion) {
        setCommunes(selectedRegion.communes);
      } else {
        setCommunes([]);
      }
    } else {
      setCommunes([]);
    }
  }, [formik.values.region]);

  // Update company communes when company region changes
  useEffect(() => {
    if (formik.values.companyRegion) {
      const selectedRegion = REGION_OPTIONS.find(
        (region) => region.region === formik.values.companyRegion,
      );
      if (selectedRegion) {
        setCompanyCommunes(selectedRegion.communes);
      } else {
        setCompanyCommunes([]);
      }
    } else {
      setCompanyCommunes([]);
    }
  }, [formik.values.companyRegion]);

  const getClientIdFromPath = () => {
    const pathParts = location.pathname.split('/');
    const clientFormIndex = pathParts.indexOf('client-form');
    if (clientFormIndex !== -1 && pathParts.length > clientFormIndex + 1) {
      return pathParts[clientFormIndex + 1]; // Return the ID
    }
    return false; // Return false if no ID is present
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);

    // Hide alert after 3 seconds
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = (actionCreator, delay = 3500) => {
    // Clear update state after 3.5 seconds
    setTimeout(() => {
      dispatch(actionCreator(null));
    }, delay);
  };

  const setClientInfo = (data) => {
    const clientData = data.data;

    // Check if company is valid
    const companyValid = clientData.isCompany && clientData.companyInfo;

    // Set all basic client fields
    formik.setValues({
      firstName: clientData.firstName || '',
      lastName: clientData.lastName || '',
      rut: clientData.rut || '',
      phone: clientData.phone || '',
      email: clientData.email || '',
      address: clientData.address || '',
      region: clientData.region || '',
      commune: clientData.commune || '',
      isCompany: clientData.isCompany || false, // Mapear isCompany
      companyName: companyValid ? clientData.companyInfo.name || '' : '',
      companyRut: companyValid ? clientData.companyInfo.rut || '' : '',
      companyAddress: companyValid ? clientData.companyInfo.address || '' : '',
      companyRegion: companyValid ? clientData.companyInfo.region || '' : '',
      companyComuna: companyValid ? clientData.companyInfo.commune || '' : '',
      interests: clientData.interests || [], // Categorías de interés del cliente (nueva estructura)
    });
  };

  useEffect(() => {
    const paramClientId = getClientIdFromPath();
    if (paramClientId && !isNaN(paramClientId)) {
      document.title = 'BGL - Actualizar Cliente';
      setClientId(paramClientId);
      dispatch(getClientById(paramClientId));
      setLoading(true);
    } else if (paramClientId && isNaN(paramClientId)) {
      document.title = 'BGL - Crear Cliente';
      handleAlert('danger', 'El ID del cliente no es válido');
      setLoading(false);
    } else if (!paramClientId) {
      document.title = 'BGL - Crear Cliente';
      setLoading(false);
    }

    // Categories will be loaded in separate useEffect
  }, []);

  useEffect(() => {
    if (clientCreateState) {
      setLoading(false);
      handleAlert(clientCreateState.status, clientCreateState.message);
      if (clientCreateState.status !== 'danger') {
        formik.resetForm();
        clearReduxState(setCreateClient);
      }
    }

    if (clientUpdateState) {
      setLoading(false);
      handleAlert(clientUpdateState.status, clientUpdateState.message);
      if (clientUpdateState.status !== 'danger') {
        // Success - no need to refetch data, form already has updated data
        clearReduxState(setUpdateClient);
      }
    }

    if (detailState) {
      if (detailState.status === 'danger') {
        handleAlert(detailState.status, detailState.message);
        setLoading(false);
        setClientId(null);
        clearReduxState(setGetClientById);
      } else if (detailState.data) {
        setClientInfo(detailState.data);
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [clientCreateState, clientUpdateState, detailState]);

  // Separate useEffect for categories loading
  useEffect(() => {
    if (!categoriesState?.data?.data && !categoriesState?.status) {
      dispatch(getCategories());
    }
  }, [categoriesState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <h2 className="mb-3">
          {clientId ? 'Editar Cliente' : 'Crear Cliente'}
        </h2>
        {clientId && (
          <>
            <strong className="ml-1">ID DEL CLIENTE:</strong> {clientId}
          </>
        )}
        <hr />
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col>
              <section className="mb-3">
                {/* Nombre */}
                <Label for="firstName">Nombre</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Nombre del cliente"
                  disabled={loading}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.firstName}
                  valid={formik.touched.firstName && !formik.errors.firstName}
                  invalid={
                    formik.touched.firstName && !!formik.errors.firstName
                  }
                />
                <FormFeedback>{formik.errors.firstName}</FormFeedback>
                <FormText>Ingrese el nombre del cliente</FormText>
              </section>
            </Col>
            <Col>
              <section className="mb-3">
                {/* Apellido */}
                <Label for="lastName">Apellido</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Apellido del cliente"
                  disabled={loading}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.lastName}
                  valid={formik.touched.lastName && !formik.errors.lastName}
                  invalid={formik.touched.lastName && !!formik.errors.lastName}
                />
                <FormFeedback>{formik.errors.lastName}</FormFeedback>
                <FormText>Ingrese el apellido del cliente</FormText>
              </section>
            </Col>
            <Col>
              <section className="mb-3">
                {/* RUT */}
                <Label for="rut">RUT</Label>
                <Input
                  id="rut"
                  type="text"
                  placeholder="RUT del cliente"
                  disabled={loading}
                  onChange={(e) => {
                    const formatted = formatFormRUT(e.target.value);
                    formik.setFieldValue('rut', formatted);
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.rut}
                  valid={formik.touched.rut && !formik.errors.rut}
                  invalid={formik.touched.rut && !!formik.errors.rut}
                />
                <FormFeedback>{formik.errors.rut}</FormFeedback>
                <FormText>Formato: 12.345.678-9 o 12345678-9</FormText>
              </section>
            </Col>
          </Row>

          <Row>
            <Col>
              <section className="mb-3">
                {/* Teléfono */}
                <Label for="phone">Teléfono</Label>
                <Input
                  id="phone"
                  type="text"
                  placeholder="Teléfono del cliente"
                  disabled={loading}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                  valid={formik.touched.phone && !formik.errors.phone}
                  invalid={formik.touched.phone && !!formik.errors.phone}
                />
                <FormFeedback>{formik.errors.phone}</FormFeedback>
                <FormText>Formato: +56912345678</FormText>
              </section>
            </Col>
            <Col>
              <section className="mb-3">
                {/* Correo electrónico */}
                <Label for="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="text"
                  placeholder="Correo electrónico del cliente"
                  disabled={loading}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  valid={formik.touched.email && !formik.errors.email}
                  invalid={formik.touched.email && !!formik.errors.email}
                />
                <FormFeedback>{formik.errors.email}</FormFeedback>
                <FormText>Formato: nombre@dominio.com</FormText>
              </section>
            </Col>
          </Row>

          <Row>
            <Col>
              <section className="mb-3">
                {/* Dirección */}
                <Label for="address">Dirección</Label>
                <Input
                  id="address"
                  type="text"
                  placeholder="Dirección del cliente"
                  disabled={loading}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.address}
                  valid={formik.touched.address && !formik.errors.address}
                  invalid={formik.touched.address && !!formik.errors.address}
                />
                <FormFeedback>{formik.errors.address}</FormFeedback>
                <FormText>Formato: Calle 123, Ciudad, Región</FormText>
              </section>
            </Col>
            <Col>
              <section className="mb-3">
                {/* Región */}
                <Label for="region">Región</Label>
                <Input
                  id="region"
                  type="select"
                  disabled={loading}
                  onChange={(e) => {
                    formik.setFieldValue('region', e.target.value);
                    formik.setFieldValue('commune', '');
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.region}
                  valid={formik.touched.region && !formik.errors.region}
                  invalid={formik.touched.region && !!formik.errors.region}
                >
                  <option value="">Selecciona una región</option>
                  {REGION_OPTIONS.map((region) => (
                    <option key={region.region} value={region.region}>
                      {region.region}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.region}</FormFeedback>
                <FormText>Selecciona la región del cliente</FormText>
              </section>
            </Col>
            <Col>
              <section className="mb-3">
                {/* Comuna */}
                <Label for="commune">Comuna</Label>
                <Input
                  id="commune"
                  type="select"
                  disabled={loading || !formik.values.region}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.commune}
                  valid={formik.touched.commune && !formik.errors.commune}
                  invalid={formik.touched.commune && !!formik.errors.commune}
                >
                  <option value="">Selecciona una comuna</option>
                  {communes.map((commune) => (
                    <option key={commune} value={commune}>
                      {commune}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.commune}</FormFeedback>
                <FormText>Selecciona la comuna del cliente</FormText>
              </section>
            </Col>
          </Row>

          <section className="mb-3">
            {/* ¿Es empresa? */}
            <Label for="isCompany" className="mr-5">
              Si quiere registrar una empresa, marcar el checkbox
            </Label>
            <Input
              id="isCompany"
              type="checkbox"
              onChange={formik.handleChange}
              checked={formik.values.isCompany}
            />
          </section>

          {formik.values.isCompany && (
            <>
              <Row>
                <Col>
                  <section className="mb-3">
                    {/* Nombre de la empresa */}
                    <Label for="companyName">Nombre de la empresa</Label>
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Nombre de la empresa"
                      disabled={loading}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.companyName}
                      valid={
                        formik.touched.companyName && !formik.errors.companyName
                      }
                      invalid={
                        formik.touched.companyName &&
                        !!formik.errors.companyName
                      }
                    />
                    <FormFeedback>{formik.errors.companyName}</FormFeedback>
                    <FormText>Ingrese el nombre de la empresa</FormText>
                  </section>
                </Col>
                <Col>
                  <section className="mb-3">
                    {/* RUT de la empresa */}
                    <Label for="companyRut">RUT de la empresa</Label>
                    <Input
                      id="companyRut"
                      type="text"
                      placeholder="RUT de la empresa"
                      disabled={loading}
                      onChange={(e) => {
                        const formatted = formatFormRUT(e.target.value);
                        formik.setFieldValue('companyRut', formatted);
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.companyRut}
                      valid={
                        formik.touched.companyRut && !formik.errors.companyRut
                      }
                      invalid={
                        formik.touched.companyRut && !!formik.errors.companyRut
                      }
                    />
                    <FormFeedback>{formik.errors.companyRut}</FormFeedback>
                    <FormText>Formato: 12.345.678-9 o 12345678-9</FormText>
                  </section>
                </Col>
              </Row>

              <Row>
                <Col>
                  <section className="mb-3">
                    {/* Dirección de la empresa  */}
                    <Label for="companyAddress">Dirección de la empresa</Label>
                    <Input
                      id="companyAddress"
                      type="text"
                      placeholder="Dirección de la empresa"
                      disabled={loading}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.companyAddress}
                      valid={
                        formik.touched.companyAddress &&
                        !formik.errors.companyAddress
                      }
                      invalid={
                        formik.touched.companyAddress &&
                        !!formik.errors.companyAddress
                      }
                    />
                    <FormFeedback>{formik.errors.companyAddress}</FormFeedback>
                    <FormText>Formato: Calle 123, Ciudad, Región</FormText>
                  </section>
                </Col>
                <Col>
                  <section className="mb-3">
                    {/* Región de la empresa */}
                    <Label for="companyRegion">Región de la empresa</Label>
                    <Input
                      id="companyRegion"
                      type="select"
                      disabled={loading}
                      onChange={(e) => {
                        formik.setFieldValue('companyRegion', e.target.value);
                        formik.setFieldValue('companyComuna', '');
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.companyRegion}
                      valid={
                        formik.touched.companyRegion &&
                        !formik.errors.companyRegion
                      }
                      invalid={
                        formik.touched.companyRegion &&
                        !!formik.errors.companyRegion
                      }
                    >
                      <option value="">Selecciona una región</option>
                      {REGION_OPTIONS.map((region) => (
                        <option key={region.region} value={region.region}>
                          {region.region}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{formik.errors.companyRegion}</FormFeedback>
                    <FormText>Selecciona la región de la empresa</FormText>
                  </section>
                </Col>
                <Col>
                  <section className="mb-3">
                    {/* Comuna de la empresa */}
                    <Label for="companyComuna">Comuna de la empresa</Label>
                    <Input
                      id="companyComuna"
                      type="select"
                      disabled={loading || !formik.values.companyRegion}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.companyComuna}
                      valid={
                        formik.touched.companyComuna &&
                        !formik.errors.companyComuna
                      }
                      invalid={
                        formik.touched.companyComuna &&
                        !!formik.errors.companyComuna
                      }
                    >
                      <option value="">Selecciona una comuna</option>
                      {companyCommunes.map((commune) => (
                        <option key={commune} value={commune}>
                          {commune}
                        </option>
                      ))}
                    </Input>
                    <FormFeedback>{formik.errors.companyComuna}</FormFeedback>
                    <FormText>Selecciona la comuna de la empresa</FormText>
                  </section>
                </Col>
              </Row>
            </>
          )}

          {/* Sección de Categorías de Interés */}
          <section className="mb-3">
            <h4 className="mb-3">Categorías de Interés</h4>
            <Label for="interests">
              Selecciona las categorías y subcategorías que le interesan al
              cliente
            </Label>
            {!categoriesState?.data?.data ||
            categoriesState.data.data.length === 0 ? (
              <div className="alert alert-info">
                <p>
                  No hay categorías disponibles. Por favor, contacta al
                  administrador para agregar categorías.
                </p>
              </div>
            ) : (
              categoriesState.data.data.map((category) => {
                const categoryInterest = formik.values.interests.find(
                  (interest) => interest.category === category.category_name,
                );
                const isCategorySelected = !!categoryInterest;
                const selectedSubcategories =
                  categoryInterest?.subcategories || [];

                return (
                  <div
                    key={category.category_value}
                    className="mb-3border p-3 rounded"
                  >
                    <Label check className="ml-2">
                      <Input
                        type="checkbox"
                        className=""
                        checked={isCategorySelected}
                        onChange={(e) => {
                          const currentInterests = formik.values.interests;
                          if (e.target.checked) {
                            // Agregar categoría con subcategorías vacías
                            formik.setFieldValue('interests', [
                              ...currentInterests,
                              {
                                category: category.category_name,
                                subcategories: [],
                              },
                            ]);
                          } else {
                            // Remover categoría y todas sus subcategorías
                            formik.setFieldValue(
                              'interests',
                              currentInterests.filter(
                                (interest) =>
                                  interest.category !== category.category_name,
                              ),
                            );
                          }
                        }}
                        disabled={loading}
                      />{' '}
                      <strong>{category.category_name}</strong>
                    </Label>

                    {/* Subcategorías */}
                    {isCategorySelected &&
                      category.types &&
                      category.types.length > 0 && (
                        <div className="ml-4 mt-2">
                          <Label className="small text-muted">
                            Subcategorías:
                          </Label>
                          {category.types.map((type, index) => (
                            <div key={index} className="ml-3">
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={selectedSubcategories.includes(
                                    type.name,
                                  )}
                                  onChange={(e) => {
                                    const currentInterests =
                                      formik.values.interests;
                                    const categoryIndex =
                                      currentInterests.findIndex(
                                        (interest) =>
                                          interest.category ===
                                          category.category_name,
                                      );

                                    if (categoryIndex !== -1) {
                                      const updatedInterests = [
                                        ...currentInterests,
                                      ];
                                      if (e.target.checked) {
                                        updatedInterests[
                                          categoryIndex
                                        ].subcategories = [
                                          ...updatedInterests[categoryIndex]
                                            .subcategories,
                                          type.name,
                                        ];
                                      } else {
                                        updatedInterests[
                                          categoryIndex
                                        ].subcategories = updatedInterests[
                                          categoryIndex
                                        ].subcategories.filter(
                                          (sub) => sub !== type.name,
                                        );
                                      }
                                      formik.setFieldValue(
                                        'interests',
                                        updatedInterests,
                                      );
                                    }
                                  }}
                                  disabled={loading}
                                />{' '}
                                <span className="small">{type.name}</span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                );
              })
            )}
            {formik.touched.interests && formik.errors.interests && (
              <div className="text-danger small">{formik.errors.interests}</div>
            )}
            <FormText>
              Selecciona al menos una categoría o subcategoría para entender los
              intereses del cliente
            </FormText>
          </section>

          <Button type="submit" disabled={!formik.isValid || loading}>
            {clientId ? 'Actualizar cliente' : 'Crear cliente'}
          </Button>
          <Button
            type="button"
            color="secondary"
            onClick={() => navigate(-1)}
          >
            Volver
          </Button>
        </Form>
      </Container>
    </>
  );
}
export default ClientForm;
